import EventEmitter from 'eventemitter3';

const eventEmitter = new EventEmitter<Record<string, unknown[]>>();

/**
 * Listener function type for event handlers.
 * Uses any[] for backward compatibility with existing typed handlers (e.g. (payload: ToastShowPayload) => void).
 */
type ListenerFn = (...args: any[]) => void;

/**
 * Emitter facade for app-wide event pub/sub.
 * Wraps EventEmitter3 with backward-compatible API and extension event behavior:
 * when an event is emitted, any registered events whose name contains the emitted
 * event name (e.g. "listen-me-another-me" for "listen-me") are also emitted,
 * except "-after" extension events unless the original event contains "-after".
 */
export const Emitter = {
  /**
   * Registers an event only once. If the event is already registered, the handler is not added again.
   */
  register(event: string, fn: ListenerFn, context?: unknown): void {
    if (!eventEmitter.eventNames().includes(event)) {
      eventEmitter.on(event, fn as (...args: unknown[]) => void, context);
    }
  },

  on(event: string, fn: ListenerFn, context?: unknown): void {
    eventEmitter.on(event, fn as (...args: unknown[]) => void, context);
  },

  once(event: string, fn: ListenerFn, context?: unknown): void {
    eventEmitter.once(event, fn as (...args: unknown[]) => void, context);
  },

  off(event: string, fn?: ListenerFn, context?: unknown): void {
    eventEmitter.off(event, fn as (...args: unknown[]) => void, context);
  },

  emit(event: string, payload?: unknown): void {
    eventEmitter.emit(event, payload);

    const extensionEvents = (eventEmitter.eventNames() as string[]).filter(
      (name) => name.includes(event) && name !== event
    );

    const filteredExtensionEvents = extensionEvents.filter((name) => {
      if (event.includes('-after')) {
        return true;
      }
      return !name.includes('-after');
    });

    for (let i = 0; i < filteredExtensionEvents.length; i++) {
      eventEmitter.emit(filteredExtensionEvents[i], payload);
    }
  },
} as const;

Object.freeze(Emitter);

export { eventEmitter };
export default Emitter;
